/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-tagless-components, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { action, computed } from '@ember/object';
import { union, setDiff } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import StaffAccessLevel from '@linc-technologies/linced-common/enums/roles/staff-page-access';
import {
  resolved,
  sortBy,
  uniq,
} from '@linc-technologies/linced-common/utils/arrays';
import { snapshotFields } from '@linc-technologies/linced-common/utils/record-rollback';
import PageType from 'linced-app/enums/page-type';

export default class PagePermissionsManager extends Component {
  @action
  setCurrentPage(value) {
    this.currentPage = value;
  }

  @action
  setPageSize(value) {
    this.pageSize = value;
  }

  @service
  session;

  @service
  store;

  @service
  toaster;

  StaffAccessLevel = StaffAccessLevel;
  PageType = PageType;

  // permissions provides the existing PagePermissions (if any) to be managed.
  // Should ideally be an AdapterPopulatedRecordArray, so that we can trigger
  // an update after saving in order to have it include newly-created records.
  permissions = null;

  // person is the Person whose Page we're managing permissions for.
  person = null;

  // page is the Page for which we're managing permissions.
  page = null;

  // emptyArray is used to prevent the search box from showing chips.
  emptyArray = Object.freeze([]);

  // pageSizes is the list of page sizes the user can choose from.
  pageSizes = Object.freeze([5, 10, 15, 20, 25]);

  // pageSize is the currently-selected page size.
  @tracked pageSize = 10;

  // pages is the list of possible pages, generated automatically.
  @computed('pageSize', 'staffWithAccess.[]')
  get pages() {
    let max = Math.max(
      1,
      Math.ceil(this.get('staffWithAccess.length') / this.pageSize),
    );
    let pages = [];
    for (var i = 1; i <= max; i++) {
      pages.push(i);
    }
    return pages;
  }

  // currentPage is the page which the user is currently viewing.
  @tracked currentPage = 1;

  // sortProp is the property on which the list is currently sorted.
  @tracked sortProp = 'firstName';

  // sortDir is the current sort direction [asc | desc].
  @tracked sortDir = 'asc';

  // paginatedAccess contains the sorted, paginated list to be shown.
  @computed(
    'currentPage',
    'pageSize',
    'sortProp',
    'sortDir',
    'staffWithAccess.[]',
  )
  get paginatedAccess() {
    let ind = (this.currentPage - 1) * this.pageSize;
    let allowedAccess = sortBy(this.staffWithAccess, this.sortProp);
    if (this.sortDir === 'desc') {
      allowedAccess = allowedAccess.reverse();
    }

    return allowedAccess.splice(ind, this.pageSize);
  }

  // saveInProgress flags whether the Page Permissions are currently being saved.
  @tracked
  saveInProgress = false;

  // existingAccess is a generated list of staff who currently have access
  // based on the PagePermissions (ignoring any unsaved changes).
  @computed('permissions.@each.allowedAccess')
  get existingAccess() {
    let merged = this.permissions.reduce((access, permission) => {
      if (!isEmpty(permission.allowedAccess)) {
        // allowedAccess is an async hasMany (a PromiseManyArray), which has
        // no slice; toggleDialog loads it first.
        access.push(...resolved(permission.allowedAccess));
      }
      return access;
    }, []);
    return uniq(merged);
  }

  // allowedAccess contains the current (unsaved) access list without leaving
  // the PagePermissions record dirty.
  @tracked
  allowedAccess = null;

  // allStaff contains an array of all staff members for the current school.
  // Automatically populated.
  @tracked
  allStaff = null;

  @union('defaultAccessStaff', 'allowedAccess')
  staffWithAccess;

  // remainingStaff contains a generated list of staff who do not yet have
  // access to the page being managed.
  @setDiff('allStaff', 'staffWithAccess')
  remainingStaff;

  @tracked
  defaultAccessStaff;

  @tracked
  dialogOpen;

  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments);
    this.store.query('person', { type: PersonType.STAFF }).then((staff) => {
      if (this.page.type === PageType.STUDENT) {
        let defaultAccessStaff = staff.filter((member) => {
          let hasAccess = this.page.authorizedStaffAccessLevels.includes(
            member.pageAccessLevel,
          );
          return hasAccess;
        });
        this.defaultAccessStaff = defaultAccessStaff;
      } else {
        // For Staff pages remove the Page's "owner" from the list,
        // as they don't need explicit permission.
        let personId = this.person.id;
        staff = staff.filter((member) => member.id !== personId);
      }
      this.allStaff = staff;
    });
  }

  // resetDialog resets the dialog state (i.e. when opening it).
  resetDialog() {
    this.currentPage = 1;
    this.saveInProgress = false;
    this.allowedAccess = this.existingAccess.slice();
  }

  // toggleDialog toggles whether the dialog is open.
  @action
  async toggleDialog() {
    if (!this.dialogOpen) {
      // Load each permission's staff list first: an unloaded list would read
      // as empty, and saving would then remove that access.
      await Promise.all(
        (this.permissions ?? []).map((permission) => permission.allowedAccess),
      );
      this.notifyPropertyChange('existingAccess');
      // Reset dialog state before opening.
      this.resetDialog();
    }
    this.toggleProperty('dialogOpen');
  }

  // addAccess adds the specified Person to the allowed access list.
  @action
  addAccess(person) {
    this.allowedAccess = [...this.allowedAccess, person];
  }

  // removeAccess removes the specified Person from the allowed access list.
  @action
  removeAccess(person) {
    this.allowedAccess = this.allowedAccess.filter((p) => p !== person);
  }

  // decrementPage moves to the previous page, if there is one.
  @action
  decrementPage() {
    if (this.currentPage > 1) {
      this.decrementProperty('currentPage');
    }
  }

  // incrementPage moves to the next page, if there is one.
  @action
  incrementPage() {
    let max = this.pages.reduce((prev, curr) => (curr > prev ? curr : prev), 0);
    if (this.currentPage < max) {
      this.incrementProperty('currentPage');
    }
  }

  // save persists the access list, in either a new PagePermission record
  // or in the first existing record.
  @action
  save() {
    this.saveInProgress = true;

    // Fetch latest page permissions, in case a record was created
    // after this dialog was opened (i.e. in another tab/browser).
    let params = {
      pageId: this.page.id,
      personId: this.person.id,
    };
    let restore;
    return this.store
      .query('posts/page-permission', params)
      .then((existing) => {
        let record = existing[0];
        if (isEmpty(record)) {
          let props = {
            schoolId: this.session.school,
            personId: this.person,
            pageId: this.page,
            allowedAccess: [],
          };
          record = this.store.createRecord('posts/page-permission', props);
        }
        restore = snapshotFields(record, ['allowedAccess']);
        record.set('allowedAccess', this.allowedAccess);

        return record.save();
      })
      .then(() => {
        this.dialogOpen = false;
        this.toaster.success('Page permissions updated successfully!');
      })
      .catch(() => {
        // The refetch below no longer drops unsaved hasMany edits, so put
        // the saved list back; the dialog keeps the edits for a retry.
        restore?.();
        this.toaster.error('Failed to update page permissions!');
      })
      .finally(() => {
        this.saveInProgress = false;
        // Guard against permissions not being an AdapterPopulatedRecordArray.
        if (!isEmpty(this.permissions.update)) {
          this.permissions.update();
        }
      });
  }
}
